import { Button, Tooltip } from '@flixbus/honeycomb-react';
import { DataTable, createDataTableColumnHelper } from '@ridehub/ui-widgets';
import { Icon, IconArrowBigRight, IconEye, IconFinance } from '@flixbus/honeycomb-icons-react';

import { NavLink } from 'react-router-dom';
import React from 'react';
import { RideSummary } from '@ridehub/data-ride';
import { printDate } from '@ridehub/util-temporal';
import styles from './rides-table.module.scss';
import { useLocalization } from '@ridehub/feat-localization';
import { useSLIContext } from '@ridehub/util-datadog';

/**
 * Interface for displaying data in table rows
 */
interface RideListRow {
    core: RideSummary['core'] & {
        route: string;
        formattedDeparture: string;
    };
    pricing: {
        localizedYield: string;
        localizedRevDistance: string;
        loadPct: string;
        demandLoadPct: string;
        bid_price_active: boolean | undefined;
    };
    meta: RideSummary['meta'];
    action: boolean;
}

interface RideTableProps {
    data: Array<RideSummary>;
    getDetailsPath: (rideId: string) => string;
    isTest?: boolean;
}

export function RidesTable(props: RideTableProps) {
    const localization = useLocalization();
    const { reportSliGoodEvent } = useSLIContext();

    const table_data: Array<RideListRow> = props.data.map((el) => {
        let pricing: RideListRow['pricing'];
        if (el.pricing) {
            const localizedRevenue = localization.localizeCurrency(el.pricing.revenue_eur, 'report');
            const rideYield = (localizedRevenue / localization.localizeDistance(el.pricing.pax_km)) * 100;
            const euroKm = localizedRevenue / localization.localizeDistance(el.pricing.distance_km);
            pricing = {
                ...el.pricing,
                loadPct: `${(el.pricing.load * 100).toFixed(0)}%`,
                demandLoadPct:
                    el.pricing.demand_load !== undefined ? `${(el.pricing.demand_load * 100).toFixed(0)}%` : '-',
                localizedRevDistance: localization.printWithLocalUnit('revenuePerDistance', euroKm, true, 'report'),
                localizedYield: localization.printWithLocalUnit('revenuePerDistance', rideYield, true, 'report'),
                bid_price_active: el.pricing.bid_price_active,
            };
        } else {
            pricing = {
                loadPct: '-',
                demandLoadPct: '-',
                localizedYield: '-',
                localizedRevDistance: '-',
                bid_price_active: false,
            };
        }
        const ride_data: RideListRow = {
            core: {
                ...el.core,
                route: `${el.core.start} -> ${el.core.destination}`,
                formattedDeparture: printDate(el.core.departure_time),
            },
            meta: {
                ...el.meta,
            },
            pricing,
            action: true,
        };
        return ride_data;
    });
    const { accessor, group } = createDataTableColumnHelper<(typeof table_data)[0]>();
    const columns = [
        group({
            header: 'Info',
            columns: [
                accessor('core.line_code', { header: 'Line Code', size: 30 }),
                accessor('core.trip_number', { header: 'TN', maxSize: 30 }),
                accessor('core.route', {
                    header: 'Route',
                    size: 900,
                    cell: ({ row }) => {
                        return (
                            <span>
                                {row.original.core.start}
                                <Icon InlineIcon={IconArrowBigRight} extraClasses={styles.routeArrow} />
                                {row.original.core.destination}
                            </span>
                        );
                    },
                }),
                accessor('core.formattedDeparture', {
                    header: 'Departure',
                    size: 600,
                    cell: ({ row }) => {
                        if (row.original.meta.data_issue) {
                            let explanation = 'This ride has missing data.';
                            if (row.original.meta.data_issue === 'overbooked') {
                                explanation = 'This ride is overbooked. KPIs can not be meaningfully calculated.';
                            }
                            if (row.original.meta.data_issue === 'zero_capcities') {
                                explanation =
                                    'This ride has segments with zero capacity. KPIs can not be meaningfully calculated.';
                            }
                            return (
                                <Tooltip id="data-issue-tooltip" content={explanation} openOnHover={true} danger={true}>
                                    <span style={{ cursor: 'help' }}>{`${row.original.core.formattedDeparture}`}</span>
                                </Tooltip>
                            );
                        }
                        return <span>{`${row.original.core.formattedDeparture}`}</span>;
                    },
                }),
            ],
        }),
        group({
            header: 'Commercial',
            columns: [
                accessor('pricing.localizedYield', { header: 'Yield', size: 300 }),
                accessor('pricing.localizedRevDistance', {
                    header: localization.localizeUnit('revenuePerDistance', false, 'report'),
                    size: 400,
                }),
            ],
        }),
        group({
            header: 'Load',
            columns: [
                accessor('pricing.demandLoadPct', { header: 'Demand', size: 30 }),
                accessor('pricing.loadPct', { header: 'Max', size: 30 }),
                accessor('pricing.bid_price_active', {
                    header: 'Bid Price',
                    size: 30,
                    cell: ({ row }) => {
                        return row.original.pricing?.bid_price_active ? (
                            <Icon InlineIcon={IconFinance} extraClasses={styles.activeBidPrice} />
                        ) : null;
                    },
                }),
            ],
        }),
        group({
            header: 'Actions',
            columns: [
                accessor('action', {
                    header: 'Details',
                    cell: ({ row }) => (
                        <Button
                            display="square"
                            aria-label="Open Details"
                            RouterLink={NavLink}
                            to={props.getDetailsPath(row.original.core.ride_id)}
                            data-dd-action-name="View Ride Details"
                        >
                            <Icon InlineIcon={IconEye} aria-hidden="true" style={{ marginLeft: 0 }} />
                        </Button>
                    ),
                }),
            ],
        }),
    ];
    reportSliGoodEvent();
    // Note: scrollable={true} allows rows to scroll under a sticky header
    // unfortunately this relies on the ResizeObserver API,
    // which is not available in the jsdom environment used in
    // component tests. Therefore this extra (ugly) prop is introduced
    // to disable this behaviour and let the table render in tests.
    return <DataTable scrollable={!props.isTest} columns={columns} data={table_data} />;
}
